import api from "@/services/api";

/**
 * @description Servicio para interactuar con el endpoint /members.
 * Gestiona el CRUD e importación masiva de afiliados de obra social.
 */
const memberService = {};

const endpoint = "/members";

/**
 * @description Obtiene listado paginado de afiliados con filtros
 * @param {Object} params - Parámetros de búsqueda
 * @param {string} [params.searchInput] - Texto de búsqueda (nombre, DNI, nº afiliado)
 * @param {string} [params.status] - Filtrar por estado (active, inactive, waiting)
 * @param {string} [params.plan] - Filtrar por plan
 * @param {number} [params.offset] - Offset para paginación
 * @param {number} [params.limit] - Límite de resultados
 * @returns {Promise<{docs: Array, total: number}>} Resultado paginado
 */
memberService.query = function (params) {
  return api.get(endpoint, { params }).then((res) => res.data);
};

/**
 * @description Obtiene un afiliado por su ID
 * @param {string} id - ID del afiliado
 * @returns {Promise<Object>} Datos del afiliado
 */
memberService.getById = function (id) {
  return api.get(`${endpoint}/${id}`).then((res) => res.data);
};

/**
 * @description Crea un nuevo afiliado
 * @param {Object} data - Datos del afiliado
 * @param {string} data.memberNumber - Número de afiliado
 * @param {string} data.dni - DNI
 * @param {string} data.firstName - Nombre
 * @param {string} data.lastName - Apellido
 * @param {string} [data.plan] - Plan
 * @param {string} [data.familyGroup] - Grupo familiar
 * @param {string} [data.status] - Estado
 * @returns {Promise<Object>} Afiliado creado
 */
memberService.create = function (data) {
  return api.post(endpoint, data).then((res) => res.data);
};

/**
 * @description Actualiza un afiliado existente
 * @param {string} id - ID del afiliado
 * @param {Object} data - Campos a actualizar
 * @returns {Promise<Object>} Afiliado actualizado
 */
memberService.update = function (id, data) {
  return api.put(`${endpoint}/${id}`, data).then((res) => res.data);
};

/**
 * @description Elimina un afiliado
 * @param {string} id - ID del afiliado
 * @returns {Promise<void>}
 */
memberService.remove = function (id) {
  return api.delete(`${endpoint}/${id}`);
};

/**
 * @description Importa afiliados desde archivo Excel/CSV
 * @param {File} file - Archivo a importar (.xlsx, .xls, .csv)
 * @returns {Promise<{imported: number, duplicates: number, total: number, errors: Array}>} Resultado
 */
memberService.import = function (file) {
  const formData = new FormData();
  formData.append("file", file);
  return api
    .post(`${endpoint}/import`, formData, {
      headers: { "Content-Type": "multipart/form-data" },
    })
    .then((res) => res.data);
};

/**
 * @description Obtiene los datos del afiliado logueado (paciente)
 * @param {Object} [options] - Opciones de request (ej. silent errors)
 * @returns {Promise<Object>} Datos del afiliado
 */
memberService.getMe = function (options = {}) {
  return api.get(`${endpoint}/me`, options).then((res) => res.data);
};

/**
 * @description Obtiene el token de credencial virtual del afiliado logueado
 * @param {Object} [options] - Opciones de request
 * @returns {Promise<Object>} Token con código y expiración
 */
memberService.getMyToken = function (options = {}) {
  return api.get(`${endpoint}/me/token`, options).then((res) => res.data);
};

/**
 * @description Obtiene el grupo familiar del afiliado logueado
 * @param {Object} [options] - Opciones de request
 * @returns {Promise<Array>} Lista de miembros del grupo familiar
 */
memberService.getMyFamily = function (options = {}) {
  return api.get(`${endpoint}/me/family`, options).then((res) => res.data);
};

/**
 * @description Genera el siguiente número de afiliado disponible para la organización.
 * Para titular: incrementa el secuencial global. Para dependiente: pasa el número del titular.
 * @param {string} [titularNumber] - Número del titular (para generar un dependiente)
 * @returns {Promise<{nextNumber: string, orgPrefix: string, nextSeq: number, personSuffix: string}>}
 */
memberService.getNextNumber = function (titularNumber) {
  const params = titularNumber ? { titularNumber } : {};
  return api.get(`${endpoint}/next-number`, { params }).then((res) => res.data);
};

/**
 * @description Reenvía la invitación de acceso a un afiliado (email + WhatsApp).
 * Si el afiliado quedó sin User, el backend lo crea e invita.
 * @param {string} id - ID del afiliado
 * @returns {Promise<{ok: boolean, created: boolean}>}
 */
memberService.resendInvitation = function (id) {
  return api
    .post(`${endpoint}/${id}/resend-invitation`)
    .then((res) => res.data);
};

export default memberService;
